import { FeatureFlags } from '../exports/Constants';

/**
 * Pure predicates for the licence paywall (PROD-494). Everything that decides
 * whether a partner is asked to pay hangs off these, so the rules can be
 * tested without React and can never drift between the five trigger screens.
 * See tests/services/tenant/providerBilling.test.js.
 */

/**
 * Whether this tenant sells licences at all: a reseller that has been migrated
 * onto platform billing. The master never pays itself, and a partner nobody
 * has migrated keeps exactly today's behaviour — a missing flags payload
 * therefore reads as "no paywall", which is the fail-safe direction.
 */
export function isLicensePaywallActive(serviceProvider) {
  return Boolean(
    serviceProvider &&
    !serviceProvider.is_master &&
    serviceProvider.enabled_feature_flags?.includes(FeatureFlags.ProviderBilling),
  );
}

/**
 * Whether this partner's restaurants charge a service fee only once it is
 * switched on for them (enabled at Go Live, or a fee of their own saved); the
 * partner default is then just a pre-fill. Legacy partners' undecided
 * restaurants inherit the partner default instead.
 *
 * Mirrors the backend's ServiceProvider::hasOptInServiceFee(), which is the
 * same white-label rule as the licence paywall above.
 */
export function hasOptInServiceFee(serviceProvider) {
  return isLicensePaywallActive(serviceProvider);
}

/**
 * Whether a paid product is already covered for this restaurant, read from the
 * entitlements payload the backend returns.
 */
export function holdsLicense(entitlements, productKey) {
  return Boolean(entitlements?.products?.find((product) => product.key === productKey)?.licensed);
}

export function findLicenseProduct(entitlements, productKey) {
  return entitlements?.products?.find((product) => product.key === productKey) ?? null;
}

/**
 * Whether the paywall should open before an action runs: only for a tenant
 * that sells licences, and only when this restaurant has not bought the
 * product yet. Everything else proceeds untouched.
 */
export function requiresLicensePurchase(serviceProvider, entitlements, productKey) {
  if (!isLicensePaywallActive(serviceProvider)) {
    return false;
  }

  return !holdsLicense(entitlements, productKey);
}

/**
 * The message a failed licence call carries, when the backend sent one. The
 * response exceptions answer `{ title, description, error_code }`; anything
 * else falls back to the caller's generic copy.
 */
export function licenseErrorMessage(error) {
  if (!error || typeof error !== 'object') {
    return null;
  }

  return error.description ?? error.title ?? null;
}

/**
 * The one currency every partner is billed in, whatever it operates in;
 * mirrors `BillingCurrency::code()` on the backend. Only a fallback — a price
 * row always names its own currency, and that wins.
 */
export const BILLING_CURRENCY = 'USD';

/**
 * The currency a price book is denominated in. Licences are priced in the
 * billing currency for every partner, whatever it operates in, so the label
 * comes from the price rows the backend returned — never from the partner's
 * or the master's own currency. A price book with no row yet (a fresh
 * environment, an unpriced product) still labels in the billing currency,
 * not in whatever the tenant happens to operate in.
 */
export function priceBookCurrency(products, fallback = BILLING_CURRENCY) {
  const priced = (products ?? []).find(
    (product) => product.override_price?.currency || product.default_price?.currency,
  );

  return priced?.override_price?.currency ?? priced?.default_price?.currency ?? fallback;
}

/** The product keys the dashboard gates on; mirrors the backend constants. */
export const LicenseProductKey = {
  PartnerPlan: 'online_ordering',
  MobileApp: 'mobile_app',
  AiWebsite: 'ai_website',
  Storefront: 'storefront',
};

/** `type` on an entitlement product; mirrors the backend enum. */
export const LicenseProductType = {
  Base: 'base',
  AddOn: 'add_on',
};

/** `price_tier` on a licence item; mirrors the backend `LicensePriceTier` enum. */
export const LicensePriceTier = {
  Standard: 'standard',
  Volume: 'volume',
  Locked: 'locked',
};

/**
 * One editable price-book row from a price resource. Amounts stay decimal
 * major units while editing; `volume_*` is the tier (both null = flat).
 */
export function priceRowFrom(price) {
  return {
    enabled: Boolean(price),
    amount: price?.amount ?? null,
    volume_amount: price?.volume_amount ?? null,
    volume_threshold: price?.volume_threshold ?? null,
  };
}

/**
 * Why a row cannot be saved, as a translation key under `price_book`, or null.
 * A tier is both numbers or neither, and the volume rate undercuts the
 * standard one — the same rules the backend enforces.
 */
export function priceRowError(product, row) {
  if (!row?.enabled) {
    return null;
  }

  if (!(row.amount > 0)) {
    return 'missing_price';
  }

  if (product.type !== LicenseProductType.Base) {
    return null;
  }

  const hasVolume = row.volume_amount !== null && row.volume_amount !== undefined;
  const hasThreshold = row.volume_threshold !== null && row.volume_threshold !== undefined;

  if (hasVolume !== hasThreshold) {
    return 'incomplete_tier';
  }

  if (hasVolume && (row.volume_amount >= row.amount || row.volume_threshold < 2)) {
    return 'invalid_tier';
  }

  return null;
}

/**
 * The PUT payload for one row, in integer minor units. Only a base product
 * carries a tier; add-ons leave the keys out so their rows stay flat.
 */
export function pricePayload(product, row) {
  const minor = (amount) => (amount === null || amount === undefined ? null : Math.round(amount * 100));
  const enabled = Boolean(row?.enabled);

  return {
    product_id: product.id,
    amount: enabled ? minor(row.amount) : null,
    ...(product.type === LicenseProductType.Base
      ? {
          volume_amount: enabled ? minor(row.volume_amount) : null,
          volume_threshold: enabled ? (row.volume_threshold ?? null) : null,
        }
      : {}),
  };
}

/** `error_code` values the paywall flows branch on; mirrors the backend exceptions. */
export const LicenseErrorCode = {
  LicenseRequired: 'license_required',
  ProviderBillingDisabled: 'provider_billing_disabled',
  OperationalStatusCheckFailed: 'operational_status_check_failed',
  PaymentMethodRequired: 'payment_method_required',
  PaymentMethodInUse: 'payment_method_in_use',
  ProviderContactRequired: 'provider_contact_required',
  TestRestaurantSlotTaken: 'test_restaurant_slot_taken',
  TestRestaurantNotBillable: 'test_restaurant_not_billable',
  // The master, or a partner on merchant billing: never sold licences, no test restaurant.
  TestRestaurantUnavailable: 'test_restaurant_unavailable',
};

/**
 * The query parameter the paywall's add-card round trip returns with. Its
 * value is the product the paywall was opened for, so only the gate that sent
 * the partner to Stripe reopens on the way back.
 */
export const PAYWALL_CARD_RETURN_PARAM = 'paywall_card';

/**
 * Set next to PAYWALL_CARD_RETURN_PARAM on Stripe's cancel URL: backing out of
 * Checkout returns to the paywall's first step rather than to a closed modal,
 * and there is no card to wait for.
 */
export const PAYWALL_CARD_CANCELLED_PARAM = 'paywall_card_cancelled';

/**
 * The free days a purchase would still start with. The backend decides
 * whether a trial is owed (once per restaurant, ever, and never on an add-on)
 * and says so through `trial_ends_at_if_activated`; `trial_days` only sizes
 * it. Zero means the offer leads with the price.
 */
export function trialDaysOwed(product) {
  if (!product?.trial_ends_at_if_activated) {
    return 0;
  }

  return product.trial_days > 0 ? product.trial_days : 0;
}

/** Stripe's lower-case brand ids, as the partner knows them. Proper nouns — never translated. */
const PAYMENT_METHOD_BRANDS = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  cartes_bancaires: 'Cartes Bancaires',
  sepa_debit: 'SEPA Direct Debit',
  link: 'Link',
  paypal: 'PayPal',
};

/**
 * "Visa •••• 4242" for the partner's billing card, from the entitlements'
 * `payment_method` (`{ brand, last4 }`). Null when there is nothing to name, so
 * the caller falls back to its own "card on file" wording instead.
 */
export function paymentMethodLabel(paymentMethod) {
  const brandId = paymentMethod?.brand?.toLowerCase();
  const brand = brandId
    ? (PAYMENT_METHOD_BRANDS[brandId] ?? brandId.replace(/_/g, ' ').replace(/^\w/, (letter) => letter.toUpperCase()))
    : null;
  const last4 = paymentMethod?.last4 ? `•••• ${paymentMethod.last4}` : null;

  return [brand, last4].filter(Boolean).join(' ') || null;
}

/**
 * What the two Go Live paths send with the launch request: the purchase and
 * the launch commit together on the backend. One constant, so the request body
 * cannot differ between the wizard and the order-settings tab.
 */
export const PARTNER_PLAN_LICENSE_PAYLOAD = Object.freeze({
  product_key: LicenseProductKey.PartnerPlan,
  terms_accepted: true,
});
